import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "../Reveal";
import { SERVICE_PAGES } from "../../lib/api";

function ExpenseConvergence() {
  const rows = ["Shipping", "Payment processing", "Operating costs", "Recovery"];
  return (
    <svg viewBox="0 0 420 240" className="w-full h-auto" role="img" aria-label="Expense categories converging into one simplified overview">
      <defs>
        <linearGradient id="ecLine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#B9CFE4" />
          <stop offset="100%" stopColor="#15B8A6" />
        </linearGradient>
      </defs>
      {rows.map((r, i) => (
        <g key={r} className="group/ecrow">
          <circle cx="34" cy={34 + i * 58} r="6" fill="#234D85" className="transition-colors duration-300 group-hover/ecrow:fill-[#15B8A6]" />
          <text x="52" y={38 + i * 58} fontSize="12" fontWeight="600" fill="#617083" style={{ fontFamily: "Inter, sans-serif" }}>{r}</text>
          <path
            d={`M46 ${34 + i * 58} C 150 ${34 + i * 58}, 220 120, 300 120`}
            fill="none" stroke="url(#ecLine)" strokeWidth="1.8"
            strokeDasharray="4 6" className="animate-dashFlow opacity-70"
          />
        </g>
      ))}
      <circle cx="318" cy="120" r="34" fill="#101F38" />
      <text x="318" y="116" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#FFFFFF" style={{ fontFamily: "Inter, sans-serif" }}>ONE CLEAR</text>
      <text x="318" y="130" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#2BD4C1" style={{ fontFamily: "Inter, sans-serif" }}>OVERVIEW</text>
      <path d="M352 120 H392" stroke="#15B8A6" strokeWidth="2" strokeDasharray="3 5" className="animate-dashFlow" />
      <circle cx="400" cy="120" r="5" fill="#15B8A6" className="animate-floatY" />
    </svg>
  );
}

function TechNetwork() {
  const nodes = [
    { x: 70, y: 60, label: "Cloud" }, { x: 190, y: 40, label: "Phones" },
    { x: 310, y: 80, label: "Connectivity" }, { x: 110, y: 170, label: "Software" },
    { x: 270, y: 185, label: "Collaboration" },
  ];
  const edges = [["0", "1"], ["1", "2"], ["0", "3"], ["3", "4"], ["1", "4"], ["2", "4"]];
  return (
    <svg viewBox="0 0 380 240" className="w-full h-auto" role="img" aria-label="Technology network: cloud, phones, connectivity, software and collaboration tools">
      {edges.map(([a, b], i) => (
        <line
          key={i}
          x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y}
          stroke="#15B8A6" strokeWidth="1.3" strokeOpacity="0.5"
          className="animate-dashFlow" strokeDasharray="4 8"
          style={{ animationDelay: `${i * 0.25}s` }}
        />
      ))}
      {nodes.map((n, i) => (
        <g key={n.label} style={{ animation: `floatY ${5 + i}s ease-in-out infinite`, animationDelay: `${i * 0.4}s` }}>
          <circle cx={n.x} cy={n.y} r="24" fill="#152845" stroke="#15B8A6" strokeOpacity="0.6" strokeWidth="1.2" />
          <circle cx={n.x} cy={n.y} r="7" fill="#2BD4C1" opacity={0.55 + (i % 3) * 0.22} />
          <text x={n.x} y={n.y + 42} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#8FB3D9" style={{ fontFamily: "Inter, sans-serif" }}>{n.label}</text>
        </g>
      ))}
    </svg>
  );
}

function BenefitsWeb() {
  const people = [{ x: 60, y: 60 }, { x: 60, y: 160 }, { x: 170, y: 110 }];
  return (
    <svg viewBox="0 0 360 220" className="w-full h-auto" role="img" aria-label="Connected people protected by benefits coverage">
      {people.map((p, i) => (
        <g key={i}>
          <path d={`M${p.x + 26} ${p.y} C 110 ${p.y}, 140 110, 196 110`} fill="none" stroke="#8FB3D9" strokeWidth="1.6" strokeDasharray="3 5" className="animate-dashFlow" style={{ animationDelay: `${i * 0.3}s` }} />
          <circle cx={p.x} cy={p.y - 8} r="11" fill="#234D85" opacity="0.9" />
          <path d={`M${p.x - 15} ${p.y + 26} C ${p.x - 15} ${p.y + 6}, ${p.x + 15} ${p.y + 6}, ${p.x + 15} ${p.y + 26} Z`} fill="#15B8A6" opacity="0.85" />
        </g>
      ))}
      <g>
        <path d="M240 66 L296 46 L296 108 C 296 140, 272 158, 240 170 C 208 158, 184 140, 184 108 L184 46 Z" fill="#EAF4F8" stroke="#15B8A6" strokeWidth="2" />
        <path d="M222 108 L234 120 L258 92" stroke="#0F9D77" strokeWidth="3.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <text x="240" y="200" textAnchor="middle" fontSize="11" fontWeight="700" fill="#617083" style={{ fontFamily: "Inter, sans-serif" }}>Care that reaches your whole team</text>
    </svg>
  );
}

function TaxScanner() {
  const rows = [74, 100, 126, 152];
  return (
    <svg viewBox="0 0 420 230" className="w-full h-auto" role="img" aria-label="Document eligibility review with scanning line and highlighted opportunities">
      <rect x="60" y="30" width="180" height="170" rx="12" fill="#FFFFFF" stroke="#DCE7EF" strokeWidth="1.5" />
      <rect x="84" y="30" width="180" height="170" rx="12" fill="#FFFFFF" stroke="#DCE7EF" strokeWidth="1.5" />
      {rows.map((y, i) => (
        <g key={y}>
          <rect x="104" y={y} width={i === 1 || i === 3 ? 96 : 128} height="12" rx="6" fill={i === 1 || i === 3 ? "#D7F2EE" : "#EAF4F8"} />
          <rect x={104 + (i === 1 || i === 3 ? 104 : 136)} y={y} width="12" height="12" rx="6" fill={i === 1 || i === 3 ? "#15B8A6" : "#C9DBEA"} />
        </g>
      ))}
      <rect x="84" y="118" width="180" height="3" fill="#15B8A6" opacity="0.9" className="animate-scanLine" style={{ transformBox: "fill-box" }} />
      <circle cx="330" cy="80" r="42" fill="#101F38" />
      <text x="330" y="76" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#FFFFFF" style={{ fontFamily: "Inter, sans-serif" }}>ELIGIBILITY</text>
      <text x="330" y="90" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#2BD4C1" style={{ fontFamily: "Inter, sans-serif" }}>REVIEW</text>
      <path d="M264 120 C 290 118, 300 100, 322 96" stroke="#15B8A6" strokeWidth="1.6" fill="none" strokeDasharray="3 5" className="animate-dashFlow" />
      <text x="330" y="150" textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#617083" style={{ fontFamily: "Inter, sans-serif" }}>Hiring · Property</text>
      <text x="330" y="166" textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#617083" style={{ fontFamily: "Inter, sans-serif" }}>Incentives</text>
    </svg>
  );
}

const cards = [
  {
    ...SERVICE_PAGES.expense,
    headline: "Find Opportunities Hidden in Everyday Expenses.",
    desc: "Many businesses have operating expenses that haven't been carefully reviewed in years. We help connect you with specialists who evaluate areas such as shipping costs, payment processing, and other potential expense-reduction or recovery opportunities.",
    cta: "Explore Business Savings",
    viz: <ExpenseConvergence />,
    span: "md:col-span-7",
    bg: "bg-white",
  },
  {
    ...SERVICE_PAGES.technology,
    headline: "Better Technology. Smarter Spending.",
    desc: "Your business technology should help you operate more effectively — not quietly drain your budget. Explore opportunities involving internet connectivity, business phone systems, cloud services, software, collaboration tools, and other technology expenses.",
    cta: "Explore Technology Savings",
    viz: <TechNetwork />,
    span: "md:col-span-5 md:row-span-2",
    bg: "bg-navy-950 text-white",
    dark: true,
  },
  {
    ...SERVICE_PAGES.benefits,
    headline: "Take Better Care of Your People.",
    desc: "Explore healthcare and employee benefit options that may help your business offer meaningful choices while managing costs. From healthcare coverage options to virtual care and additional benefits, we can help you connect with specialized resources.",
    cta: "Explore Benefits Options",
    viz: <BenefitsWeb />,
    span: "md:col-span-5",
    bg: "bg-ice",
  },
  {
    ...SERVICE_PAGES.tax,
    headline: "Discover Opportunities You May Have Overlooked.",
    desc: "Depending on your business activities, hiring, investments, or property ownership, you may qualify for certain tax credits or incentives. We connect you with specialists who can help evaluate potential opportunities.",
    cta: "Explore Tax Opportunities",
    viz: <TaxScanner />,
    span: "md:col-span-7",
    bg: "bg-white",
  },
];

export default function ServiceBento() {
  return (
    <section className="relative py-16 md:py-24 bg-ice/60" data-testid="services-bento">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <SectionLabel>What We Help You Explore</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
              Four Ways to Strengthen Your Bottom Line
            </h2>
          </Reveal>
        </div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-5 md:auto-rows-[minmax(300px,auto)]">
          {cards.map((c, i) => (
            <Reveal key={c.key} delay={i * 0.07} className={`${c.span} h-full`}>
              <Link
                to={c.path}
                data-testid={`bento-service-${c.key}-card`}
                className={`group relative flex flex-col h-full rounded-3xl border p-6 md:p-8 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${c.bg} ${
                  c.dark ? "border-white/10 shadow-lift" : "border-line shadow-card hover:shadow-lift"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-[11px] font-bold uppercase tracking-wider2 ${c.dark ? "text-teal-bright" : "text-royal"}`}>
                    0{i + 1} — {c.card}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 text-[13px] font-bold ${c.dark ? "text-teal-bright" : "text-teal-dark"}`}>
                    {c.cta}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
                <h3 className={`mt-4 font-display font-extrabold tracking-tight text-xl md:text-2xl leading-snug ${c.dark ? "text-white" : "text-navy"}`}>
                  {c.headline}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed ${c.dark ? "text-ice/70" : "text-slate"}`}>{c.desc}</p>
                <div className="mt-auto pt-6">{c.viz}</div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
